import { exportWorkbookSheets, sanitizeFileName } from "./exportWorkbook";

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("en-GB");
}

function getLeadTypeLabel(type) {
  if (type === "ACCEPT") return "Lead";
  if (type === "NOT ACCEPT") return "Discard";
  return "Unsure";
}

function buildCommentExportText(comments) {
  if (!Array.isArray(comments) || comments.length === 0) {
    return "";
  }

  return comments
    .map((comment) => {
      const author = comment.userId || "Unknown";
      const createdAt = formatDateTime(comment.createdAt) || "Unknown time";
      return `${author} (${createdAt}): ${comment.message || ""}`;
    })
    .join(" ; ");
}

function formatFundingRoundAmount(amount, currency) {
  if (amount === undefined || amount === null || amount === "") {
    return "Unknown";
  }

  if (typeof amount === "string") {
    return amount;
  }

  const normalizedCurrency = currency || "USD";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: normalizedCurrency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function toPeopleSummary(extraction) {
  const people = Array.isArray(extraction?.people) ? extraction.people : [];
  return people
    .map((person) => {
      const name = person.name || person.personName || person.person_name || "Unknown";
      const title =
        person.title ||
        person.explicitTitleJob ||
        person.explicit_title_job ||
        "";
      return title && title !== "Unknown" ? `${name} (${title})` : name;
    })
    .join(" ; ");
}

function toFundingRoundsSummary(extraction) {
  const fundingRounds = Array.isArray(extraction?.fundingRounds)
    ? extraction.fundingRounds
    : [];

  return fundingRounds
    .map((round) => {
      const roundName = round.round || round.roundName || round.round_name || "Unknown";
      const date = round.date || round.announcedDate || round.announced_date || "";
      const amount = formatFundingRoundAmount(round.amount, round.currency);
      return [roundName, date, amount].filter(Boolean).join(" | ");
    })
    .join(" ; ");
}

function getLatestFundingRound(extraction) {
  const fundingRounds = Array.isArray(extraction?.fundingRounds)
    ? extraction.fundingRounds
    : [];

  const normalized = fundingRounds
    .map((round) => ({
      roundName: round.round || round.roundName || round.round_name || "Unknown",
      date: round.date || round.announcedDate || round.announced_date || "",
      leadInvestors: round.leadInvestors || round.lead_investors || [],
    }))
    .sort((left, right) => String(right.date || "").localeCompare(String(left.date || "")));

  return normalized[0] || null;
}

function readCrunchbaseField(company, field, context) {
  const feedback = context.feedbackData[company.id] || {};
  const comments = context.commentsByCompany.get(company.id) || [];
  const extraction = context.extractionsByCompany.get(company.id) || null;
  const latestRound = getLatestFundingRound(extraction);
  const originalType =
    feedback.previousLeadType || company["Original Lead Type"] || company["Lead Type"];

  switch (field) {
    case "organizationName":
      return company["Organization Name"];
    case "originalLeadType":
      return getLeadTypeLabel(originalType);
    case "correctedLeadType":
      return getLeadTypeLabel(company["Lead Type"]);
    case "teamComments":
      return buildCommentExportText(comments);
    case "lastUpdated": {
      const latestCommentTimestamp =
        comments.length > 0
          ? comments
              .map((comment) => comment.createdAt)
              .filter(Boolean)
              .sort((a, b) => new Date(b) - new Date(a))[0]
          : null;
      return latestCommentTimestamp
        ? formatDateTime(latestCommentTimestamp)
        : feedback.timestamp
        ? formatDateTime(feedback.timestamp)
        : "";
    }
    case "justification":
      return company["Reasoning"] || "";
    case "industries":
      return Array.isArray(company["Industries"])
        ? company["Industries"].join("; ")
        : company["Industries"] || "";
    case "bucketIndustries":
      return (company.BucketIndustries || []).join("; ");
    case "country":
      return company["Headquarters Location"] || "";
    case "founded":
      return company["Founded"] || "";
    case "employees":
      return company["Employees"] || "";
    case "investmentStage":
      return company["Investment Stage"] || "";
    case "estimatedRevenueRange":
      return company["Estimated Revenue Range"] || "";
    case "lastFundingDate":
      return company["Last Funding Date"] || "";
    case "totalFundingAmount":
      return company["Total Funding Amount"] || "";
    case "crunchbaseRank":
      return company["Crunchbase Rank"] ?? "";
    case "investorType":
      return company["Investor Type"] || "";
    case "topInvestors":
      return company["Top 5 Investors"] || "";
    case "leadInvestors":
      return company["Lead Investors"] || "";
    case "website":
      return company["Website"] || "";
    case "crunchbaseUrl":
      return company["Crunchbase"] || "";
    case "globaldataMatch":
      return company["GlobalData Matching Name"] || "";
    case "companyHouseMatch":
      return company["Company House Matching Name"] || "";
    case "clientMatch":
      return company["Salesforce Client Matching Name"] || "";
    case "peopleSummary":
      return toPeopleSummary(extraction);
    case "fundingRoundsSummary":
      return toFundingRoundsSummary(extraction);
    case "latestFundingRound":
      return latestRound?.roundName || "";
    case "latestFundingRoundDate":
      return latestRound?.date || "";
    case "latestFundingRoundLeadInvestors":
      return Array.isArray(latestRound?.leadInvestors)
        ? latestRound.leadInvestors.join("; ")
        : "";
    default:
      return "";
  }
}

async function loadCommentsByCompany(companies, getApiJson) {
  if (!getApiJson) {
    return new Map();
  }

  const commentEntries = await Promise.all(
    companies.map(async (company) => {
      try {
        const comments = await getApiJson(
          `/api/leads/lead-context/CRUNCHBASE/company/${encodeURIComponent(
            company["Organization Name"]
          )}/comments`
        );
        return [company.id, Array.isArray(comments) ? comments : []];
      } catch {
        return [company.id, []];
      }
    })
  );

  return new Map(commentEntries);
}

async function loadExtractionsByCompany(companies, getApiJson) {
  if (!getApiJson) {
    return new Map();
  }

  const extractionEntries = await Promise.all(
    companies.map(async (company) => {
      try {
        const extraction = await getApiJson(
          `/api/leads/lead-context/CRUNCHBASE/company/${encodeURIComponent(
            company["Organization Name"]
          )}/extraction`
        );
        return [company.id, extraction || null];
      } catch {
        return [company.id, null];
      }
    })
  );

  return new Map(extractionEntries);
}

export default async function exportFeedbackToWorkbook({
  companies,
  feedbackData,
  selectedFields,
  fileName = "company_feedback_export",
  format = "xlsx",
  getApiJson,
  fieldOptions,
  exportFilters = [],
}) {
  const needsComments = selectedFields.some((field) =>
    ["teamComments", "lastUpdated"].includes(field)
  );
  const needsExtractions = selectedFields.some((field) =>
    [
      "peopleSummary",
      "fundingRoundsSummary",
      "latestFundingRound",
      "latestFundingRoundDate",
      "latestFundingRoundLeadInvestors",
    ].includes(field)
  );

  const [commentsByCompany, extractionsByCompany] = await Promise.all([
    needsComments ? loadCommentsByCompany(companies, getApiJson) : new Map(),
    needsExtractions ? loadExtractionsByCompany(companies, getApiJson) : new Map(),
  ]);

  const fieldLabels = new Map(fieldOptions.map((option) => [option.value, option.label]));
  const rows = companies.map((company) => {
    const row = {};
    selectedFields.forEach((field) => {
      row[fieldLabels.get(field) || field] = readCrunchbaseField(company, field, {
        feedbackData,
        commentsByCompany,
        extractionsByCompany,
      });
    });
    return row;
  });

  if (format === "csv") {
    const headers = selectedFields.map((field) => fieldLabels.get(field) || field);
    const escapeCsvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csvRows = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(",")),
    ];
    const blob = new Blob([csvRows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${sanitizeFileName(fileName, "company_feedback_export")}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    return;
  }

  exportWorkbookSheets(
    [
      {
        sheetName: "Export Filters",
        rows: (exportFilters || []).map((row) => ({
          Filter: row.filter || "",
          Value: row.value || "",
        })),
      },
      { sheetName: "Crunchbase", rows },
    ],
    fileName
  );
}
